import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import apiBase from "../../utils/apiBase";

// ── FlowOS embed ──────────────────────────────────────────────────────────────
// The full FlowOS app (chat + Process Debate + admin) lives in its own repo and
// is deployed independently (Railway). We embed it here as an iframe so the
// FlowOS frontend can be developed and shipped without touching this backend.
//
// Flow: GET /api/flowos/session (authenticated by the BFB JWT) returns the
// FlowOS URL + a short-lived SSO token. We pass the token in the iframe URL
// fragment (#sso=…); FlowOS validates it, maps it to the shared proxy key
// server-side, and gates services by our subscription tier. The proxy key never
// reaches the browser.

const BASE = `${apiBase()}/api/flowos`;

export default function FlowOS({ user }) {
  const history = useHistory();
  const [src, setSrc] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user?.id) {
      history.push("/login");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${BASE}/session`, {
          headers: { "auth-token": localStorage.getItem("auth") },
        });
        if (!res.ok) throw new Error(`Session request failed (${res.status})`);
        const { url, token } = await res.json();
        if (!url || !token) throw new Error("Malformed session response");
        if (!cancelled) {
          // Fragment (not query) keeps the token out of FlowOS server logs.
          setSrc(`${url.replace(/\/$/, "")}/#sso=${encodeURIComponent(token)}`);
        }
      } catch (err) {
        if (!cancelled) setError(err.message || "Could not start FlowOS");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, history]);

  if (error) {
    return (
      <div style={styles.message}>
        <p style={{ fontWeight: 600, marginBottom: 8 }}>FlowOS is unavailable</p>
        <p style={{ color: "#6B7280", fontSize: 14 }}>{error}</p>
      </div>
    );
  }

  if (!src) {
    return <div style={styles.message}>Loading FlowOS…</div>;
  }

  return (
    <iframe
      title="FlowOS"
      src={src}
      style={styles.iframe}
      allow="clipboard-write"
    />
  );
}

const styles = {
  iframe: {
    width: "100%",
    height: "100%",
    border: "none",
    display: "block",
  },
  message: {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
    color: "#111",
  },
};
